/**
 * 1.3.74: MVP 付费功能 - 套餐升级页面
 * 显示套餐对比，支持选择套餐并跳转到 Stripe Checkout
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, 
  Check, 
  Crown, 
  Users, 
  Building2, 
  Loader2,
  AlertCircle,
  Minus,
  Plus
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  getOrganizationSubscription, // 1.4.5: 使用组织ID获取订阅
  redirectToCheckout,
  formatPrice,
} from '../services/billingService';
import { getCurrentOrganization } from '../services/organizationService';
import { PLAN_DISPLAY_CONFIGS } from '../types/billing';
import type { SubscriptionInfo } from '../types/billing';
import { logger } from '../utils/logger';

export function UpgradePlanPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // 状态
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<'individual' | 'standard' | null>(null);
  const [memberCount, setMemberCount] = useState(2); // Standard 最低 2 人
  const [isProcessing, setIsProcessing] = useState(false);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      // 1.4.5: 先获取当前组织，再用组织ID获取订阅信息
      const orgResult = await getCurrentOrganization();
      
      if (orgResult.organization) {
        setOrganizationId(orgResult.organization.id);
        
        // 1.4.5: 使用组织ID获取订阅，确保获取正确的组织订阅状态
        const subResult = await getOrganizationSubscription(orgResult.organization.id);
        
        if (subResult.subscription) {
          setSubscription(subResult.subscription);
        
          // 1.3.81: 根据订阅状态自动选中合适的套餐
          const sub = subResult.subscription;
          const isTrialing = sub.subscription_status === 'trialing';
          const isActive = sub.subscription_status === 'active';
        
          if (sub.plan_type === 'individual' && isActive) {
            // 付费个人账号：自动选中 Standard
            setSelectedPlan('standard');
          } else if (sub.plan_type === 'standard') {
            // 企业账号（无论试用还是付费）：自动选中 Standard
            setSelectedPlan('standard');
            // 设置当前已购买的人数
            if (sub.purchased_seats) {
              setMemberCount(sub.purchased_seats);
            }
          }
          // 试用中个人账号：不自动选中，让用户选择
        }
      }
    } catch (error) {
      logger.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 计算选中套餐的月费
  const calculateMonthlyPrice = () => {
    if (!selectedPlan) return 0;
    
    if (selectedPlan === 'individual') {
      return 2500;
    } else {
      return 2300 * memberCount;
    }
  };
  
  // 处理升级
  const handleUpgrade = async () => {
    if (!selectedPlan || !organizationId) {
      toast.error(t('pleaseSelectPlan', '请选择套餐'));
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const error = await redirectToCheckout({
        organizationId,
        plan: selectedPlan,
        quantity: selectedPlan === 'standard' ? memberCount : 1,
      });
      
      if (error) {
        toast.error(error.message);
        setIsProcessing(false);
      }
      // 如果成功，页面会跳转到 Stripe
    } catch (error: any) {
      logger.error('Error redirecting to checkout:', error);
      toast.error(t('upgradeError', '升级失败，请稍后重试'));
      setIsProcessing(false);
    }
  };
  
  // 获取套餐配置（过滤掉 free）
  const plans = PLAN_DISPLAY_CONFIGS.filter(p => p.id !== 'free');
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-5xl mx-auto py-8 px-6">
        {/* 返回按钮 */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('back', '返回')}</span>
        </button>
        
        {/* 1.3.81: 页面标题 - 根据订阅状态显示不同标题 */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center gap-3">
            <Crown className="w-8 h-8 text-primary" />
            {(() => {
              const isTrialing = subscription?.subscription_status === 'trialing';
              const isActive = subscription?.subscription_status === 'active';
              const isIndividual = subscription?.plan_type === 'individual';
              const isStandard = subscription?.plan_type === 'standard';
              
              if (isStandard && isActive) {
                // 付费企业账号：调整席位
                return t('adjustSeats', '调整席位');
              } else if (isIndividual && isActive) {
                // 付费个人账号：升级到 Standard
                return t('upgradeToBusiness', '升级到 Standard');
              } else {
                // 试用中：升级账号
                return t('upgradeAccount', '升级账号');
              }
            })()}
          </h1>
          <p className="text-gray-600 mt-2">
            {(() => {
              const isActive = subscription?.subscription_status === 'active';
              const isStandard = subscription?.plan_type === 'standard';
              
              if (isStandard && isActive) {
                return t('adjustSeatsDescription', '增加或减少团队席位数量');
              } else {
                return t('choosePlanDescription', '选择适合您的套餐，解锁更多功能');
              }
            })()}
          </p>
        </div>
        
        {/* 当前套餐提示 */}
        {subscription && (
          <div className="mb-8 p-4 bg-gray-100 rounded-lg text-center">
            <span className="text-gray-600">
              {t('currentPlan', '当前套餐')}：
            </span>
            <span className="font-semibold text-gray-900 ml-2">
              {subscription.plan_type === 'free' ? 'Free' : 
               subscription.plan_type === 'individual' ? 'Individual' : 'Standard'}
            </span>
            {subscription.subscription_status === 'trialing' && (
              <span className="ml-3 px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded">
                {t('trialPeriod', '试用中')}
              </span>
            )}
          </div>
        )}
        
        {/* 1.3.81: 套餐卡片 - 根据订阅状态限制可选套餐
         * 试用中个人账号：Individual 和 Standard 两者都可选择
         * 付费个人账号：只能选择 Standard（Individual 不可点击）
         * 试用中/付费企业账号：只能选择 Standard
         */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            const isTrialing = subscription?.subscription_status === 'trialing';
            const isActive = subscription?.subscription_status === 'active';
            const currentPlanType = subscription?.plan_type;
            
            // 判断此套餐是否不可选择
            let isDisabled = false;
            let disabledReason = '';
            
            if (plan.id === 'individual') {
              // Individual 套餐不可选的情况：
              // 1. 当前是付费 Individual（已经是该套餐了）
              // 2. 当前是 Standard（无论试用还是付费，不能降级）
              if (currentPlanType === 'individual' && isActive) {
                isDisabled = true;
                disabledReason = 'current';
              } else if (currentPlanType === 'standard') {
                isDisabled = true;
                disabledReason = 'higher';
              } else if (currentPlanType === 'individual' && isTrialing) {
                // 试用中个人账号，Individual 可选
                isDisabled = false;
              }
            } else if (plan.id === 'standard') {
              // Standard 套餐不可选的情况：
              // 当前是付费 Standard（已经是该套餐了）
              if (currentPlanType === 'standard' && isActive) {
                isDisabled = true;
                disabledReason = 'current';
              }
            }
            
            // 是否显示"当前套餐"标签
            const showCurrentLabel = disabledReason === 'current';
            
            return (
              <div
                key={plan.id}
                onClick={() => !isDisabled && setSelectedPlan(plan.id as 'individual' | 'standard')}
                className={`relative p-6 rounded-xl border-2 transition-all ${
                  isDisabled
                    ? 'border-gray-300 bg-gray-50 cursor-not-allowed opacity-60'
                    : isSelected
                    ? 'border-primary bg-primary/5 shadow-lg cursor-pointer'
                    : 'border-gray-200 bg-white hover:border-primary/50 hover:shadow-md cursor-pointer'
                }`}
              >
                {/* 推荐标签 - 只在非禁用状态显示 */}
                {plan.recommended && !isDisabled && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-primary text-white text-sm font-medium rounded-full">
                      {t('recommended', '推荐')}
                    </span>
                  </div>
                )}
                
                {/* 已订阅标签 */}
                {showCurrentLabel && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-green-500 text-white text-sm font-medium rounded-full">
                      {t('currentPlan', '当前套餐')}
                    </span>
                  </div>
                )}
                
                {/* 套餐图标和名称 - 1.3.80: 多语言支持 */}
                <div className="flex items-center gap-3 mb-4">
                  {plan.id === 'individual' ? (
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{t(plan.nameKey, plan.id)}</h3>
                    <p className="text-sm text-gray-500">{t(plan.descriptionKey, plan.descriptionFallback)}</p>
                  </div>
                </div>
                
                {/* 价格 - 1.3.80: 多语言支持 */}
                <div className="mb-6">
                  <span className="text-3xl font-bold text-gray-900">
                    {formatPrice(plan.price, false).replace('/月', '')}
                  </span>
                  <span className="text-gray-500 ml-1">{t(plan.priceUnitKey, plan.priceUnitFallback)}</span>
                </div>
                
                {/* 功能列表 - 1.3.80: 多语言支持 */}
                <ul className="space-y-3 mb-6">
                  {plan.featureKeys.map((featureKey, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{t(featureKey, plan.featureFallbacks[index])}</span>
                    </li>
                  ))}
                </ul>
                
                {/* 选中指示 */}
                {isSelected && !isDisabled && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Standard 套餐人数选择 */}
        {selectedPlan === 'standard' && (
          <div className="mb-8 p-6 bg-white rounded-xl border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('selectMemberCount', '选择团队人数')}
            </h3>
            
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setMemberCount(Math.max(2, memberCount - 1))}
                  disabled={memberCount <= 2}
                  className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Minus className="w-5 h-5" />
                </button>
                
                <div className="w-20 text-center">
                  <span className="text-3xl font-bold text-gray-900">{memberCount}</span>
                  <span className="text-gray-500 ml-1">{t('people', '人')}</span>
                </div>
                
                <button
                  onClick={() => setMemberCount(Math.min(50, memberCount + 1))}
                  disabled={memberCount >= 50}
                  className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex-1">
                <input
                  type="range"
                  min="2"
                  max="50"
                  value={memberCount}
                  onChange={(e) => setMemberCount(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>2{t('people', '人')}</span>
                  <span>50{t('people', '人')}</span>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-gray-500 mt-4">
              {t('standardPriceNote', 'Standard 套餐按人数计费，最低 2 人起')}
            </p>
          </div>
        )}
        
        {/* 价格汇总 - 1.3.80: 多语言支持 */}
        {selectedPlan && (
          <div className="mb-8 p-6 bg-primary/5 rounded-xl border border-primary/20">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('orderSummary', '订单摘要')}
                </h3>
                <p className="text-gray-600">
                  {selectedPlan === 'individual' 
                    ? t('orderSummaryIndividual', 'Individual 套餐 × 1') 
                    : t('orderSummaryStandard', 'Standard 套餐 × {{count}} 人', { count: memberCount })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">{t('monthlyTotal', '每月合计')}</p>
                <p className="text-3xl font-bold text-primary">
                  {formatPrice(calculateMonthlyPrice(), false)}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* 操作按钮 */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('cancel', '取消')}
          </button>
          
          <button
            onClick={handleUpgrade}
            disabled={!selectedPlan || isProcessing}
            className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('processing', '处理中...')}
              </>
            ) : (
              <>
                <Crown className="w-5 h-5" />
                {t('proceedToPayment', '前往支付')}
              </>
            )}
          </button>
        </div>
        
        {/* 安全提示 */}
        <div className="mt-8 p-4 bg-gray-100 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-700 mb-1">
              {t('securePayment', '安全支付')}
            </p>
            <p>
              {t('paymentNote', '您将被重定向到 Stripe 安全支付页面完成支付。支持信用卡、借记卡等多种支付方式。订阅后可随时在账户中心管理或取消。')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
